import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, Plus, Trash2, GripVertical, PlayCircle, 
  FileText, HelpCircle, Sparkles, Loader2, Save, Eye
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CourseBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const isEditing = !!id;

  const [course, setCourse] = useState({
    title: '',
    description: '',
    category: 'programming',
    level: 'beginner',
    price: 29.99,
    thumbnail_url: ''
  });
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [lessonDialog, setLessonDialog] = useState(false);
  const [currentLesson, setCurrentLesson] = useState({
    title: '',
    content_type: 'video',
    content: '',
    duration_minutes: 0,
    is_preview: false
  });

  const categories = ['programming', 'design', 'business', 'marketing', 'photography', 'music'];
  const levels = ['beginner', 'intermediate', 'advanced'];

  useEffect(() => {
    if (isEditing) {
      fetchCourse();
    }
  }, [id]);

  const fetchCourse = async () => {
    try {
      const response = await axios.get(`${API}/courses/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      setCourse({
        title: data.title,
        description: data.description,
        category: data.category,
        level: data.level,
        price: data.price,
        thumbnail_url: data.thumbnail_url
      });
      setLessons(data.lessons || []);
    } catch (error) {
      toast.error('Course not found');
      navigate('/instructor');
    } finally {
      setLoading(false);
    }
  };

  const generateDescription = async () => {
    if (!course.title) {
      toast.error('Please enter a course title first');
      return;
    }

    setGeneratingAI(true);
    try {
      const response = await axios.post(`${API}/ai/generate-description`, {
        title: course.title,
        category: course.category,
        level: course.level,
        topics: []
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourse({ ...course, description: response.data.description });
      toast.success('Description generated!');
    } catch (error) {
      toast.error('Failed to generate description');
    } finally {
      setGeneratingAI(false);
    }
  };

  const saveCourse = async () => {
    if (!course.title || !course.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        await axios.put(`${API}/courses/${id}`, course, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Course updated!');
      } else {
        const response = await axios.post(`${API}/courses`, course, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Course created!');
        navigate(`/instructor/courses/${response.data.id}/edit`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  const addLesson = async () => {
    if (!currentLesson.title || !currentLesson.content) {
      toast.error('Please fill in lesson title and content');
      return;
    }

    try {
      const response = await axios.post(`${API}/courses/${id}/lessons`, {
        ...currentLesson,
        order: lessons.length
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLessons([...lessons, response.data]);
      setLessonDialog(false);
      setCurrentLesson({ title: '', content_type: 'video', content: '', duration_minutes: 0, is_preview: false });
      toast.success('Lesson added!');
    } catch (error) {
      toast.error('Failed to add lesson');
    }
  };

  const deleteLesson = async (lessonId) => {
    if (!window.confirm('Delete this lesson?')) return;

    try {
      await axios.delete(`${API}/courses/${id}/lessons/${lessonId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLessons(lessons.filter(l => l.id !== lessonId));
      toast.success('Lesson deleted');
    } catch (error) {
      toast.error('Failed to delete lesson');
    }
  };

  const getLessonIcon = (type) => {
    switch (type) {
      case 'video': return <PlayCircle className="w-4 h-4" />;
      case 'text': return <FileText className="w-4 h-4" />;
      case 'quiz': return <HelpCircle className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="course-builder">
      <Navbar />

      <div className="pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/instructor')}
                className="-ml-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <h1 className="font-heading text-2xl font-bold text-slate-900">
                {isEditing ? 'Edit Course' : 'Create Course'}
              </h1>
            </div>
            <Button 
              onClick={saveCourse} 
              disabled={saving}
              className="bg-primary hover:bg-primary/90"
              data-testid="save-course-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Course
                </>
              )}
            </Button>
          </div>

          {/* Course Details */}
          <Card className="mb-6 border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading">Course Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="title">Course Title *</Label>
                <Input
                  id="title"
                  value={course.title}
                  onChange={(e) => setCourse({ ...course, title: e.target.value })}
                  placeholder="e.g., Complete Python Masterclass"
                  data-testid="course-title-input"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="description">Description *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={generateDescription}
                    disabled={generatingAI}
                    data-testid="generate-description-btn"
                  >
                    {generatingAI ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        AI Generate
                      </>
                    )}
                  </Button>
                </div>
                <Textarea
                  id="description"
                  value={course.description}
                  onChange={(e) => setCourse({ ...course, description: e.target.value })}
                  placeholder="Describe what students will learn..."
                  rows={5}
                  data-testid="course-description-input"
                />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select
                    value={course.category}
                    onValueChange={(value) => setCourse({ ...course, category: value })}
                  >
                    <SelectTrigger data-testid="course-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat.charAt(0).toUpperCase() + cat.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Level</Label>
                  <Select
                    value={course.level}
                    onValueChange={(value) => setCourse({ ...course, level: value })}
                  >
                    <SelectTrigger data-testid="course-level-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {levels.map((lvl) => (
                        <SelectItem key={lvl} value={lvl}>
                          {lvl.charAt(0).toUpperCase() + lvl.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="price">Price ($)</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    value={course.price}
                    onChange={(e) => setCourse({ ...course, price: parseFloat(e.target.value) })}
                    data-testid="course-price-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="thumbnail">Thumbnail URL</Label>
                <Input
                  id="thumbnail"
                  value={course.thumbnail_url}
                  onChange={(e) => setCourse({ ...course, thumbnail_url: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                  data-testid="course-thumbnail-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Lessons Section - Only show when editing */}
          {isEditing && (
            <Card className="border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading">Course Lessons</CardTitle>
                <Dialog open={lessonDialog} onOpenChange={setLessonDialog}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="add-lesson-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Lesson
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Add New Lesson</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Lesson Title</Label>
                        <Input
                          value={currentLesson.title}
                          onChange={(e) => setCurrentLesson({ ...currentLesson, title: e.target.value })}
                          placeholder="Introduction to Python"
                          data-testid="lesson-title-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Content Type</Label>
                        <Select
                          value={currentLesson.content_type}
                          onValueChange={(value) => setCurrentLesson({ ...currentLesson, content_type: value })}
                        >
                          <SelectTrigger data-testid="lesson-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="video">Video</SelectItem>
                            <SelectItem value="text">Text/Article</SelectItem>
                            <SelectItem value="quiz">Quiz</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>
                          {currentLesson.content_type === 'video' 
                            ? 'Video URL' 
                            : currentLesson.content_type === 'quiz'
                            ? 'Quiz JSON'
                            : 'Content'
                          }
                        </Label>
                        <Textarea
                          value={currentLesson.content}
                          onChange={(e) => setCurrentLesson({ ...currentLesson, content: e.target.value })}
                          placeholder={
                            currentLesson.content_type === 'video' 
                              ? 'https://youtube.com/watch?v=...'
                              : currentLesson.content_type === 'quiz'
                              ? '{"questions": [{"question": "...", "options": [...], "correct": "..."}]}'
                              : 'Write your lesson content here...'
                          }
                          rows={4}
                          data-testid="lesson-content-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Duration (minutes)</Label>
                        <Input
                          type="number"
                          value={currentLesson.duration_minutes}
                          onChange={(e) => setCurrentLesson({ ...currentLesson, duration_minutes: parseInt(e.target.value) || 0 })}
                          data-testid="lesson-duration-input"
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <Label htmlFor="is-preview" className="font-medium">Free Preview</Label>
                          <p className="text-xs text-slate-500">Allow non-buyers to preview this lesson</p>
                        </div>
                        <Switch
                          id="is-preview"
                          checked={currentLesson.is_preview}
                          onCheckedChange={(checked) => setCurrentLesson({ ...currentLesson, is_preview: checked })}
                          data-testid="lesson-preview-switch"
                        />
                      </div>
                      <Button onClick={addLesson} className="w-full" data-testid="save-lesson-btn">
                        Add Lesson
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {lessons.length > 0 ? (
                  <div className="space-y-2">
                    {lessons.sort((a, b) => a.order - b.order).map((lesson, index) => (
                      <div 
                        key={lesson.id}
                        className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg"
                        data-testid={`lesson-item-${lesson.id}`}
                      >
                        <GripVertical className="w-4 h-4 text-slate-400 cursor-move" />
                        <span className="w-8 h-8 flex items-center justify-center bg-white rounded-full text-sm font-medium text-slate-600 border border-slate-200">
                          {index + 1}
                        </span>
                        <div className="flex items-center gap-2 text-slate-600">
                          {getLessonIcon(lesson.content_type)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-900">{lesson.title}</p>
                            {lesson.is_preview && (
                              <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                                <Eye className="w-3 h-3 mr-1" />
                                Preview
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 capitalize">
                            {lesson.content_type} • {lesson.duration_minutes} min
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteLesson(lesson.id)}
                          className="text-slate-400 hover:text-destructive"
                          data-testid={`delete-lesson-${lesson.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600">No lessons yet. Add your first lesson to get started.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseBuilder;
